import {
  Apps,
  StickerApps,
  JPSingleApps,
  NekuApps,
  GameApps,
  UnityApps,
  IndependentApps,
  StickerMainApps,
  StickerSubApps,
  TotalsApp,
  TotalsJPApp,
  App0,
  Apps1,
  SubscribeApps,
  BaselineCountryCodes,
} from './app'
import { PGames, Products1, Products, PID_GROUP, Countries } from './index'

export const PRODUCT_TREE_URL = '/products/tree'
export const PRODUCT_TREE_ADMIN_URL = '/products/tree/admin'

// 产品线分组码 → 旧前端 group 名；未列出的分组沿用分组码
const LEGACY_GROUP_NAMES = {
  total1: 'sticker',
  total2: 'neku',
  total3: 'jpsingle',
  total4: 'unity',
  independent: 'independent',
  game: 'game',
}
const LEGACY_SUB_NAMES = { total1_1: 'main', total1_2: 'sub' }
// game 分组历史上单列在 PGames，不进 Apps / TotalsApp
const SEPARATE_GROUP = 'game'
// Products 里保留的分组聚合项（ROI 对比表、权限矩阵的列）
const PRODUCT_GROUP_CODES = ['total1', 'total1_1', 'total1_2', 'total2']
const JP_GROUP_CODES = ['total1', 'total1_1', 'total1_2', 'total3']
const SUBSCRIBE_PIDS = ['11', '12']

const replace = (target, items) => {
  target.splice(0, target.length, ...items)
}

/** 深度优先遍历分组（按接口给出的 sort 顺序），回调参数 (group, ancestors) */
export const walkGroups = (groups = [], visit, ancestors = []) => {
  groups.forEach((group) => {
    visit(group, ancestors)
    walkGroups(group.children, visit, [...ancestors, group])
  })
}

/** 分组下所有后代 pid（字符串），按树顺序 */
export const collectPids = (group) => {
  const pids = []
  walkGroups([group], (g) => {
    (g.pids ?? []).forEach((p) => pids.push(String(p.pid)))
  })
  return pids
}

/**
 * 当前用户可读的国家码集合：树上任一节点的 countries 并集；含 '*' 时返回 null 表示全部。
 */
export const readableCountryCodes = (groups = []) => {
  const codes = new Set()
  let all = false
  walkGroups(groups, (g) => {
    const lists = [g.countries ?? [], ...(g.pids ?? []).map((p) => p.countries ?? [])]
    lists.forEach((list) =>
      list.forEach((code) => {
        if (code === '*') all = true
        else codes.add(code)
      }),
    )
  })
  return all ? null : codes
}

export const countryLabel = (country) =>
  country.core ? `${country.name} *` : country.name

/** 把 /products/tree 的结果写入 comm 下的常量（原地刷新，引用不变） */
export const applyProductTree = ({ groups = [], countries = [] } = {}) => {
  const apps = []
  const games = []
  const totals = [{ code: 'total', name: 'All' }]
  const groupNodes = {}
  const pidGroup = {}

  walkGroups(groups, (group, ancestors) => {
    if (group.kind === 'root') return
    const chain = [...ancestors.filter((g) => g.kind !== 'root'), group]
    const line = chain[0].code
    const sub = chain.find((g) => LEGACY_SUB_NAMES[g.code])?.code
    groupNodes[group.code] = group
    pidGroup[group.code] = collectPids(group)

    if (line !== SEPARATE_GROUP && group.countries?.length > 0) {
      const prefix = group.kind === 'sub' ? '⮑' : ''
      totals.push({ code: group.code, name: `${prefix}${group.name}` })
    }

    (group.pids ?? []).forEach((p) => {
      const app = {
        code: String(p.pid),
        name: p.name,
        group: LEGACY_GROUP_NAMES[line] ?? line,
        ...(sub && { sub: LEGACY_SUB_NAMES[sub] }),
      }
      if (line === SEPARATE_GROUP) games.push(app)
      else apps.push(app)
    })
  })

  const byGroup = (name) => apps.filter((app) => app.group === name)

  replace(Apps, apps)
  replace(StickerApps, byGroup('sticker'))
  replace(JPSingleApps, byGroup('jpsingle'))
  replace(NekuApps, byGroup('neku'))
  replace(GameApps, byGroup('game'))
  replace(UnityApps, byGroup('unity'))
  replace(IndependentApps, byGroup('independent'))
  replace(
    StickerMainApps,
    apps.filter((app) => app.group === 'sticker' && app.sub === 'main'),
  )
  replace(
    StickerSubApps,
    apps.filter((app) => app.group === 'sticker' && app.sub === 'sub'),
  )
  replace(TotalsApp, totals)
  replace(TotalsJPApp, [
    { code: 'totalJP', name: 'All' },
    ...totals.filter((t) => JP_GROUP_CODES.includes(t.code)),
    ...StickerMainApps,
    ...StickerSubApps,
  ])
  replace(App0, [{ code: 'total', name: 'All' }, ...apps])
  replace(Apps1, [...totals, ...apps])
  replace(
    SubscribeApps,
    apps
      .filter((app) => SUBSCRIBE_PIDS.includes(app.code))
      .map(({ code, name }) => ({ code, name })),
  )

  replace(PGames, games)
  replace(Products1, [...totals, ...StickerApps, ...NekuApps])
  replace(Products, [
    { code: 'total', name: 'Total' },
    ...PRODUCT_GROUP_CODES.filter((code) => groupNodes[code]).map((code) => ({
      code,
      name: groupNodes[code].name,
      pids: pidGroup[code],
    })),
    ...apps,
  ])
  Object.keys(PID_GROUP).forEach((code) => delete PID_GROUP[code])
  Object.assign(PID_GROUP, pidGroup)

  const sortedCountries = [...countries].sort(
    (a, b) => (a.sort ?? 0) - (b.sort ?? 0),
  )
  replace(Countries, [
    { code: 'total', name: 'Total' },
    ...sortedCountries.map((c) => ({ code: c.code, name: countryLabel(c) })),
  ])
  replace(
    BaselineCountryCodes,
    sortedCountries.filter((c) => c.baseline).map((c) => c.code),
  )
}
